import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "../../context/TenantContext";
import "./CreatorLanding.css";

// Brand name shown on the page, taken from the resolved tenant so every
// tenant's creator landing carries its own name rather than a hardcoded one.
const useBrandName = () => useTenant()?.tenant?.name || "the platform";

const NAV_LINKS = [
  { id: "how", label: "How it works" },
  { id: "paths", label: "Membership" },
  { id: "earn", label: "Ways to earn" },
  { id: "reach", label: "Reach" },
];

const FAQS = [
  {
    q: "Who can join?",
    a: "Anyone with a story of grief, loss or healing to share — or a community to share it with. Filmmakers, counselors, coaches, charities, podcasters and support groups all use one account.",
  },
  {
    q: "What can I upload?",
    a: "Films, series, documentaries, talks and shows — as long as you hold the rights to distribute them.",
  },
  {
    q: "How does my referral link work?",
    a: "Every account gets a personal referral link. Link opens, sign-ups and paid subscriptions that come through it are credited to you, and you can see each one in your dashboard.",
  },
  {
    q: "How do I earn and track it?",
    a: "Two ways: your share of the Watch Pool (based on minutes watched on your titles) and referral commission. Track both in your dashboard. Earnings are not guaranteed.",
  },
  {
    q: "When do I get paid?",
    a: "Earnings are calculated monthly and paid to your connected Stripe account once you pass the minimum balance. Your dashboard shows the status of every payout.",
  },
];

// Room for the floating nav (8px offset + 62px bar) plus breathing space.
const NAV_OFFSET = 92;

// Scroll so the section's content (not its top padding) lands just under the
// nav — otherwise the padding plus a strip of the previous section show above it.
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (id === "top") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const content = el.querySelector(":scope > .wrap") || el;
  const y = content.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
}

function ChevronDown() {
  return (
    <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function UpArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Nav({ scrolled, menuOpen, onToggleMenu, onNavClick, onLogoClick }) {
  const { logo } = useTenant();
  const brandName = useBrandName();
  return (
    <header className={`nav${scrolled ? " scrolled" : ""}`} id="nav">
      <div className="nav-inner">
        <a className="brand" href="#top" onClick={(e) => { e.preventDefault(); onLogoClick(); }} aria-label={`${brandName} home`}>
          <img className="brand-logo" src={logo} alt={brandName} />
        </a>

        <nav className={`nav-links${menuOpen ? " open" : ""}`} id="navlinks" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a key={link.id} className="link" href={`#${link.id}`} onClick={(e) => onNavClick(e, link.id)}>
              {link.label}
            </a>
          ))}
          <div className="nav-cta mobile">
            <Link className="btn btn-ghost" to="/creator-login">Sign in</Link>
            <Link className="btn btn-primary" to="/creator-signup">Join {brandName}</Link>
          </div>
        </nav>

        <div className="nav-cta desktop">
          <Link className="nav-signin" to="/creator-login">Sign in</Link>
          <Link className="btn btn-primary" to="/creator-signup">
            Join {brandName}
            <svg className="arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </Link>
        </div>

        <button
          className="nav-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="navlinks"
          onClick={onToggleMenu}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {menuOpen ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 8h16M4 16h16" />}
          </svg>
        </button>
      </div>
    </header>
  );
}

function Hero({ heroPlay }) {
  const proof = ["One account for everything", "Personal referral link", "Monthly payouts"];
  return (
    <section className="hero">
      <div className="hero-bg" aria-hidden="true">
        <div className="aura" />
        <div className="ember" />
        <div className="lines" />
      </div>
      <div className="wrap">
        <div className={`hero-copy reveal-hero${heroPlay ? " play" : ""}`}>
          <span className="hero-eyebrow">
            <span className="tag">Creators &amp; Partners</span>
            A streaming home for grief &amp; healing
          </span>
          <h1>
            Your story could help{" "}
            <span className="hl">someone heal.</span>
          </h1>
          <p className="hero-sub">
            Share films and conversations about loss, and earn from the time people spend watching.
          </p>
          <div className="hero-actions">
            <Link className="btn btn-primary lg" to="/creator-signup">
              Start sharing
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Link>
            <a className="btn btn-ghost lg" href="#how" onClick={(e) => { e.preventDefault(); scrollToId("how"); }}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg>
              How it works
            </a>
          </div>
          <ul className="hero-proof">
            {proof.map((p) => <li key={p}><CheckIcon /> {p}</li>)}
          </ul>
        </div>

        <div className="hero-stage" aria-hidden="true">
          <div className="hs-back" />
          <div className="hs-poster">
            <div className="hs-sun" />
            <svg className="hs-hills" viewBox="0 0 400 200" preserveAspectRatio="none">
              <path d="M0 120c60-30 110-40 170-18s120 10 230-34v132H0Z" fill="#2B1654" />
              <path d="M0 150c80-26 150-30 220-8s110 6 180-18v76H0Z" fill="#1B0E35" />
              <path d="M0 176c90-18 170-18 250-4s100 4 150-6v34H0Z" fill="#0E0819" />
            </svg>
            <div className="hs-top">
              <span className="hs-chip live"><i /> Now streaming</span>
            </div>
            <div className="hs-info">
              <span className="hs-genre">Documentary series</span>
              <h3>Letters to Mum</h3>
              <p>A daughter's year of goodbyes</p>
              <div className="hs-player">
                <span className="hs-play"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg></span>
                <span className="hs-track"><i /></span>
                <span className="hs-time">24:10</span>
              </div>
            </div>
          </div>

          <div className="hs-float link">
            <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></svg></span>
            <span>
              <span className="k">Via your referral link</span>
              <span className="v">New sign-up</span>
            </span>
          </div>

          <div className="hs-float earn">
            <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></svg></span>
            <span>
              <span className="k">Eligible earnings</span>
              <span className="v">$300</span>
              <span className="up"><UpArrow /> 11.3% this month</span>
            </span>
          </div>

          <span className="hs-note">Sample data for illustration</span>
        </div>
      </div>
    </section>
  );
}

function TrustedStrip() {
  const who = [
    { label: "Filmmakers", d: <><path d="M4 8h16v11H4z" /><path d="m8 8-2-4h12l-2 4" /></> },
    { label: "Grief counselors", d: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /> },
    { label: "Coaches", d: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.5" /></> },
    { label: "Support groups", d: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.2A6.5 6.5 0 0 1 21.5 20" /></> },
    { label: "Podcasters", d: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6" /></> },
    { label: "Faith & community", d: <><path d="M3 21h18M5 21V10l7-6 7 6v11" /><path d="M10 21v-5h4v5" /></> },
    { label: "Wellness creators", d: <><path d="M5 19c0-8 6-14 15-14 0 9-6 15-14 15" /><path d="M5 19 13 11" /></> },
    { label: "Charities & hospices", d: <><path d="M12 21V11" /><path d="M12 11c0-4 3-7 7-7 0 4-3 7-7 7ZM12 13c0-3-2.5-5.5-6-5.5 0 3.5 2.5 5.5 6 5.5Z" /></> },
    { label: "Writers & poets", d: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></> },
  ];
  // Rendered twice so the ticker can loop seamlessly; the copy is hidden from AT.
  const renderSet = (hidden) => (
    <ul className="tk-set" aria-hidden={hidden || undefined}>
      {who.map((w) => (
        <li className="tk-item" key={w.label}>
          <span className="tk-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{w.d}</svg></span>
          {w.label}
          <span className="tk-sep" aria-hidden="true">✦</span>
        </li>
      ))}
    </ul>
  );
  return (
    <div className="trusted">
      <div className="wrap tk-row">
        <div className="tk-label">
          <span className="tk-count">One home</span>
          <span>for every kind of<br />storyteller</span>
        </div>
        <div className="tk-viewport">
          <div className="tk-track">
            {renderSet(false)}
            {renderSet(true)}
          </div>
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { title: "Create your account", tag: "Apply", body: "Apply in minutes — one account for everything.", d: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></> },
    { title: "Add your stories", tag: "Upload", body: "Upload films and series. We review each title.", d: <><path d="M12 15V3m0 0 4 4m-4-4L8 7" /><path d="M4 17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2" /></> },
    { title: "Share where it matters", tag: "Your link", body: "Send your referral link to your community.", d: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></> },
    { title: "Watch it grow", tag: "Dashboard", body: "Track views, watch time and earnings.", d: <><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></> },
  ];
  return (
    <section className="section how-section" id="how">
      <div className="wrap">
        <div className="hw-head">
          <div>
            <span className="kicker"><span className="dot" /> How it works</span>
            <h2 className="ehead">Four steps from your first upload to your first eligible earning</h2>
          </div>
          <div className="hw-aside">
            <p>A clear path from your story to the people who need it — here's what happens after you join.</p>
            <Link className="hw-link" to="/creator-signup">
              Start with step one
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Link>
          </div>
        </div>

        <ol className="hw-track">
          {steps.map((s, i) => (
            <li className="hw-step" key={s.title}>
              <div className="hw-node">
                <span className="hw-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{s.d}</svg></span>
              </div>
              <div className="hw-body">
                <div className="hw-meta">
                  <span className="hw-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="hw-tag">{s.tag}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Membership() {
  const brandName = useBrandName();
  const ways = [
    { key: "upload", title: "Upload", body: "Share your films and talks on grief and healing.", d: <><path d="M12 15V3m0 0 4 4m-4-4L8 7" /><path d="M4 17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2" /></> },
    { key: "promote", title: "Promote", body: "Share your personal referral link and earn from the paid sign-ups it brings.", d: <><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></> },
    { key: "pool", title: "Watch Pool", body: "Earn a share of the monthly pool, based on minutes watched.", d: <><path d="M3 7c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /><path d="M3 13c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /></> },
  ];
  const included = ["Per-title analytics", "Stripe payouts", "Earnings ledger export"];
  return (
    <section className="section mb-section" id="paths">
      <div className="wrap">
        <div className="mb-head">
          <span className="kicker"><span className="dot" /> One membership</span>
          <h2 className="ehead">One account. <span className="grad-text">Every way to earn.</span></h2>
        </div>

        <div className="mb-panel">
          <div className="mb-ways">
            {ways.map((w) => (
              <div className={`mb-way ${w.key}`} key={w.key} id={w.key === "pool" ? "watchpool" : undefined}>
                <span className="mb-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{w.d}</svg></span>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
          <div className="mb-foot">
            <ul className="mb-inc">
              {included.map((item) => <li key={item}><CheckIcon /> {item}</li>)}
            </ul>
            <Link className="btn btn-primary" to="/creator-signup">
              Join {brandName}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Link>
          </div>
        </div>
        <p className="mb-note">Earnings aren't guaranteed and depend on eligibility and revenue-sharing rules.</p>
      </div>
    </section>
  );
}

function AffiliateFlow({ flowRef, flowAnimate }) {
  const brandName = useBrandName();
  const pillars = [
    { title: "A link for every title", body: "Each film, series or talk gets its own unique link, tied to your account the moment you create it.", d: <><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></> },
    { title: "Shared where healing happens", body: "Support groups, counseling sessions, memorial pages, newsletters — wherever your community gathers.", d: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /> },
    { title: "Credited to you, automatically", body: "Clicks, views and watch time from your link are attributed to you and shown in your dashboard.", d: <><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></> },
  ];
  const channels = ["WhatsApp", "Instagram", "Facebook groups", "YouTube", "Email & newsletters", "Websites & blogs"];
  return (
    <section className="section earnings-section" id="earnings">
      <div className="wrap">
        <div className="section-head">
          <span className="kicker"><span className="dot" /> How affiliate earnings work</span>
          <h2 className="ehead">Every share can reach someone who needs it — and earn for you</h2>
          <p className="lead">Every Creator and Partner on {brandName} receives unique tracking links, so eligible activity can be attributed to the right account.</p>
        </div>

        <div className={`share-grid${flowAnimate ? " animate" : ""}`} id="affiliateFlow" ref={flowRef}>
          {pillars.map((p) => (
            <div className="share-card" key={p.title}>
              <div className="sc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p.d}</svg></div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>

        <div className="share-channels">
          <span className="sc-label">Share anywhere</span>
          {channels.map((c) => <span className="sc-chip" key={c}>{c}</span>)}
        </div>

        <p className="flow-note">Attribution links the whole journey — from the moment someone opens your link to the eligible activity it generates — back to your account, automatically.</p>
      </div>
    </section>
  );
}

function MultipleWaysToEarn() {
  const streams = [
    {
      key: "pool",
      title: "Watch Pool",
      body: "A share of the monthly pool, based on minutes watched on your titles.",
      measure: "Minutes watched",
      d: <><path d="M3 7c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /><path d="M3 13c3 0 3 3 6 3s3-3 6-3 3 3 6 3" /></>,
    },
    {
      key: "ref",
      title: "Referral commission",
      body: "Earn when people who join through your link buy a paid plan.",
      measure: "Paid sign-ups",
      d: <><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></>,
    },
  ];
  return (
    <section className="section earn-section" id="earn">
      <div className="wrap">
        <span className="kicker"><span className="dot" /> Ways to earn</span>
        <h2 className="ehead">Two ways to earn, <span className="grad-text">one account.</span></h2>
        <p className="lead">Earn from what people watch and who you bring in.</p>

        <div className="ew-pair">
          {streams.map((s, i) => (
            <Fragment key={s.key}>
              {i > 0 && <span className="ew-plus" aria-hidden="true">+</span>}
              <article className={`ew-card ${s.key}`}>
                <span className="ew-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{s.d}</svg></span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <span className="ew-measure">Based on <b>{s.measure}</b></span>
              </article>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhereStoriesReach() {
  const brandName = useBrandName();
  const places = ["Support groups", "Memorial pages", "Newsletters", "Social media"];
  const arrow = (
    <span className="wr-link-line" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
    </span>
  );
  return (
    <section className="section reach-section" id="reach">
      <div className="wrap">
        <div className="wr-head">
          <span className="kicker"><span className="dot" /> Where stories reach</span>
          <h2 className="ehead">Share where <span className="grad-text">healing happens.</span></h2>
          <p className="lead">Your personal referral link brings people from the places they turn to when grieving — straight to {brandName}.</p>
        </div>

        <div className="wr-flow">
          <div className="wr-step wr-story">
            <span className="wr-lbl">Your channel</span>
            <div className="wr-poster" aria-hidden="true">
              <span className="wr-play"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg></span>
            </div>
            <span className="wr-name">Your films &amp; series</span>
          </div>

          {arrow}

          <div className="wr-step wr-linkbox">
            <span className="wr-lbl">Your referral link</span>
            <div className="wr-url">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></svg>
              <span>…/?ref=<b>you</b></span>
            </div>
          </div>

          {arrow}

          <div className="wr-step wr-places">
            <span className="wr-lbl">Shared anywhere</span>
            <ul>
              {places.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        </div>

        <p className="wr-foot">
          <CheckIcon />
          Link opens, sign-ups and paid subscriptions through your link are credited to your account.
        </p>
      </div>
    </section>
  );
}

function WhyJoin() {
  const brandName = useBrandName();
  const items = [
    { title: "Earn from your stories", body: "Turn your films and talks into ongoing income.", d: <path d="M8 5v14l11-7L8 5Z" /> },
    { title: "Reach those who need it", body: "Connect with viewers looking for comfort.", d: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /> },
    { title: "Simple uploads", body: "Manage your whole library in one place.", d: <><path d="M12 15V3m0 0 4 4m-4-4L8 7" /><path d="M4 17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2" /></> },
    { title: "Clear analytics", body: "See views, watch time and earnings.", d: <><path d="M3 3v18h18" /><path d="M7 15v-4M12 15V9M17 15v-7" /></> },
  ];
  return (
    <section className="section why-section" id="why">
      <div className="wrap">
        <span className="kicker"><span className="dot" /> Why {brandName}</span>
        <h2 className="ehead">Everything you need, in one place</h2>
        <div className="why-row">
          {items.map((it) => (
            <div className="why-item" key={it.title}>
              <div className="wi"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{it.d}</svg></div>
              <h3>{it.title}</h3>
              <p>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhoCanJoin() {
  const tags = [
    "Filmmakers & studios",
    "Counselors & therapists",
    "Coaches",
    "Charities & hospices",
    "Faith communities",
    "Podcasters & writers",
    "Support groups",
  ];
  return (
    <section className="section who-section" id="who">
      <div className="wrap">
        <div className="who-card">
          <div className="who-copy">
            <span className="kicker"><span className="dot" /> Who can join</span>
            <h2>If you have a story to share, you belong here</h2>
          </div>
          <div className="who-tags">
            {tags.map((t) => <span className="who-chip" key={t}>{t}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function Onboarding() {
  const steps = [
    { title: "Create your account", body: "Accept your invite and sign up." },
    { title: "Verify your identity", body: "ID or business registration." },
    { title: "Add titles & rights", body: "Upload content with its documents." },
    { title: "Choose a revenue model", body: "Pick how each title earns." },
    { title: "Go live", body: "We review, approve and publish." },
  ];
  const ready = [
    "Proof of ownership",
    "Copyright document",
    "Distribution / license agreement",
    "Music & clip licenses",
    "Cast & location releases",
    "Payout & tax details",
  ];
  return (
    <section className="section onb-section" id="onboarding">
      <div className="wrap onb-grid">
        <div>
          <span className="kicker"><span className="dot" /> Onboarding</span>
          <h2 className="ehead">From invite to live in five steps</h2>
          <p className="lead">We verify ownership and rights before anything goes live.</p>
          <ol className="onb-steps">
            {steps.map((s, i) => (
              <li key={s.title}>
                <span className="n">{i + 1}</span>
                <div><h3>{s.title}</h3><p>{s.body}</p></div>
              </li>
            ))}
          </ol>
        </div>

        <div className="onb-ready">
          <h3>Have these ready</h3>
          <ul>
            {ready.map((r) => <li key={r}><CheckIcon /> {r}</li>)}
          </ul>
          <p className="onb-note">Requirements vary by content and region.</p>
        </div>
      </div>
    </section>
  );
}

const REVENUE_MODELS = [
  { id: "free", title: "Free", abbr: "Free", line: "Anyone can watch — the widest possible audience for your story.", viewers: "Watch for free", earn: "Builds reach — no charge to viewers", best: "Maximum reach", d: <><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /></> },
  { id: "svod", title: "Subscription", abbr: "SVOD", line: "Included for {brand} subscribers.", viewers: "Watch with a subscription", earn: "Watch Pool share, by minutes watched", best: "A loyal audience", d: <><path d="M3 7l9-4 9 4-9 4-9-4Z" /><path d="M3 12l9 4 9-4M3 17l9 4 9-4" /></> },
];

function RevenueModels() {
  const brandName = useBrandName();
  const [selected, setActive] = useState(0);
  // Clamp: a stale index (e.g. kept by hot reload after the list shrank) falls back to the first option.
  const active = selected < REVENUE_MODELS.length ? selected : 0;
  const m = REVENUE_MODELS[active];
  return (
    <section className="section rev-section" id="revenue">
      <div className="wrap">
        <div className="rv-head">
          <span className="kicker"><span className="dot" /> Access options</span>
          <h2 className="ehead">Choose how <span className="grad-text">each title is watched.</span></h2>
          <p className="lead">Set access for every title when you publish it — free for everyone, or included for subscribers.</p>
        </div>

        <div className="rv-picker" role="tablist" aria-label="Access options">
          {REVENUE_MODELS.map((x, i) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              id={`rv-tab-${x.id}`}
              aria-selected={active === i}
              aria-controls="rv-panel"
              className={`rv-pill${active === i ? " on" : ""}`}
              onClick={() => setActive(i)}
            >
              <span className="rv-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{x.d}</svg></span>
              {x.title}
            </button>
          ))}
        </div>

        <div className="rv-panel" id="rv-panel" role="tabpanel" aria-labelledby={`rv-tab-${m.id}`} key={m.id}>
          <div className="rv-main">
            <span className="rv-abbr">{m.abbr}</span>
            <h3>{m.title}</h3>
            <p>{m.line.replace("{brand}", brandName)}</p>
          </div>
          <dl className="rv-facts">
            <div><dt>Viewers</dt><dd>{m.viewers}</dd></div>
            <div><dt>You earn</dt><dd>{m.earn}</dd></div>
            <div className="best"><dt>Best for</dt><dd>{m.best}</dd></div>
          </dl>
        </div>

        <p className="rv-note">
          Your referral link works whatever access you choose. Earnings aren't guaranteed and depend on eligibility and revenue-sharing rules.
        </p>
      </div>
    </section>
  );
}

function FaqSection({ openFaq, onToggle, faqRefs }) {
  const brandName = useBrandName();
  return (
    <section className="section faq-section" id="faq">
      <div className="wrap faq-grid">
        <div className="faq-head">
          <span className="kicker"><span className="dot" /> Good to know</span>
          <h2 className="ehead">Everything you need <span className="grad-text">before you begin.</span></h2>
          <p className="lead">Straight answers about joining, sharing and getting paid.</p>
          <div className="faq-help">
            <div className="fh-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /></svg>
            </div>
            <h3>Ready to share your story?</h3>
            <p>Create your account in minutes.</p>
            <div className="fh-actions">
              <Link className="btn btn-primary" to="/creator-signup">Join {brandName}</Link>
              <Link className="btn btn-ghost" to="/creator-login">Sign in</Link>
            </div>
          </div>
        </div>
        <div className="faq">
          {FAQS.map((item, i) => {
            const open = openFaq === i;
            return (
              <div className={`faq-item${open ? " open" : ""}`} key={item.q}>
                <button className="faq-q" aria-expanded={open} onClick={() => onToggle(i)}>
                  {item.q}
                  <ChevronDown />
                </button>
                <div
                  className="faq-a"
                  ref={(el) => { faqRefs.current[i] = el; }}
                  style={{ maxHeight: open ? `${faqRefs.current[i]?.scrollHeight || 600}px` : "0px" }}
                >
                  <div className="faq-a-inner">{item.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="section final">
      <div className="fbg" aria-hidden="true" />
      <div className="wrap final-inner">
        <h2>Ready to turn your content or audience into an opportunity?</h2>
        <p>Join our Creator &amp; Partner network today.</p>
        <div className="hero-actions">
          <Link className="btn btn-primary lg" to="/creator-signup">Get started</Link>
        </div>
        <p className="signin">Already a member? <Link to="/creator-login">Sign in</Link></p>
      </div>
    </section>
  );
}

function Footer({ onNavClick, onLogoClick }) {
  const { logo } = useTenant();
  const brandName = useBrandName();
  return (
    <footer className="ft">
      <div className="wrap">
        <div className="ft-top">
          <div className="ft-brand">
            <a className="brand" href="#top" onClick={(e) => { e.preventDefault(); onLogoClick(); }} aria-label={`${brandName} home`}>
              <img className="brand-logo" src={logo} alt={brandName} />
            </a>
            <p>A compassionate home for stories of grief, loss and healing.</p>
          </div>
          <div className="ft-cols">
            <div className="ft-col">
              <h5>Explore</h5>
              <a href="#how" onClick={(e) => onNavClick(e, "how")}>How it works</a>
              <a href="#reach" onClick={(e) => onNavClick(e, "reach")}>Where stories reach</a>
              <a href="#faq" onClick={(e) => onNavClick(e, "faq")}>FAQ</a>
            </div>
            <div className="ft-col">
              <h5>Account</h5>
              <Link to="/creator-signup">Get started</Link>
              <Link to="/creator-login">Sign in</Link>
            </div>
          </div>
        </div>
        <div className="ft-bottom">
          <span>© {new Date().getFullYear()} {brandName}. All rights reserved.</span>
          <a href="#top" className="ft-top-link" onClick={(e) => { e.preventDefault(); onLogoClick(); }}>
            Back to top
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 15 6-6 6 6" /></svg>
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function CreatorLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroPlay, setHeroPlay] = useState(false);
  const [flowAnimate, setFlowAnimate] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const flowRef = useRef(null);
  const faqRefs = useRef([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroPlay(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !flowRef.current || !("IntersectionObserver" in window)) return undefined;
    const node = flowRef.current;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setFlowAnimate(entry.isIntersecting)),
      { threshold: 0.2 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToId(id);
  };

  const handleLogoClick = () => {
    setMenuOpen(false);
    scrollToId("top");
  };

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <div className="creator-landing">
      <Nav
        scrolled={scrolled}
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((o) => !o)}
        onNavClick={handleNavClick}
        onLogoClick={handleLogoClick}
      />
      <main id="top">
        <Hero heroPlay={heroPlay} />
        <TrustedStrip />
        <HowItWorks />
        <Membership />
        {/* <AffiliateFlow flowRef={flowRef} flowAnimate={flowAnimate} /> */}
        <MultipleWaysToEarn />
        <WhereStoriesReach />
        {/* <WhyJoin /> */}
        {/* <WhoCanJoin /> */}
        {/* <Onboarding /> */}
        <RevenueModels />
        <FaqSection openFaq={openFaq} onToggle={toggleFaq} faqRefs={faqRefs} />
        {/* <FinalCta /> */}
      </main>
      <Footer onNavClick={handleNavClick} onLogoClick={handleLogoClick} />
    </div>
  );
}
